import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Pagination,
  IconButton
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useMediaQuery } from "@mui/material";
import api from "../utils/api";
import CameraModal from "./CameraModal";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";

const Attendance = () => {
  const [ip, setIp] = useState("");
  const [location, setLocation] = useState(null);
  const [status, setStatus] = useState("out"); // "in" or "out"
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [cameraModalOpen, setCameraModalOpen] = useState(false);
  const [actionType, setActionType] = useState(null); // "check-in" or "check-out"
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  // Helper: Format working hours as "X hrs Y mins"
  const formatWorkingHours = (checkIn, checkOut) => {
    const diffMs = new Date(checkOut) - new Date(checkIn);
    const diffMinutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;
    return `${hours} hrs ${minutes} mins`;
  };

  const fetchAttendanceRecords = async () => {
    try {
      const response = await api.get("/punch");
      const data = response.data;
      setAttendanceRecords(data);
      setLoading(false);
      // Reset to first page when new data is fetched.
      setPage(1);
      return data;
    } catch (err) {
      setError(err.response?.data?.message || "Error fetching attendance records.");
      setLoading(false);
      return null;
    }
  };

  useEffect(() => {
    fetchAttendanceRecords();
  }, []);

  useEffect(() => {
    if (attendanceRecords && attendanceRecords.length > 0) {
      const sortedRecords = [...attendanceRecords].sort(
        (a, b) => new Date(b.checkIn) - new Date(a.checkIn)
      );
      const latestRecord = sortedRecords[0];
      if (latestRecord.checkIn && (!latestRecord.checkOut || latestRecord.checkOut === "")) {
        setStatus("in");
      } else {
        setStatus("out");
      }
    } else {
      setStatus("out");
    }
  }, [attendanceRecords]);

  // Get IP address.
  useEffect(() => {
    fetch("https://api.ipify.org?format=json")
      .then((res) => res.json())
      .then((data) => {
        if (data.ip) setIp(data.ip);
      })
      .catch(() => setError("Error fetching IP address."));
  }, []);

  // Get geolocation.
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setLocation({
            latitude: pos.coords.latitude.toFixed(4),
            longitude: pos.coords.longitude.toFixed(4),
          });
        },
        (err) => {
          console.error("Geolocation error:", err);
          setError("Error fetching geolocation data.");
        }
      );
    } else {
      setError("Geolocation is not supported by this browser.");
    }
  }, []);

  // Convert base64 image string to File.
  const base64ToFile = (base64, filename) => {
    try {
      if (!base64.includes(",")) {
        throw new Error("Invalid base64 string");
      }
      const arr = base64.split(",");
      const mimeMatch = arr[0].match(/:(.*?);/);
      if (!mimeMatch) {
        throw new Error("Invalid MIME type");
      }
      const mime = mimeMatch[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new File([u8arr], filename, { type: mime });
    } catch (error) {
      setError("Error processing image data.");
      return null;
    }
  };

  const handleCapture = async (capturedImage) => {
    setCameraModalOpen(false);
    const formData = new FormData();

    if (!location) {
      setError("Location data is required.");
      return;
    }

    if (typeof capturedImage === "string" && capturedImage.startsWith("data:image")) {
      const fileName = `${actionType}-${Date.now()}.jpg`;
      const fileImage = base64ToFile(capturedImage, fileName);
      if (!fileImage) {
        return; // Error already set in base64ToFile.
      }
      capturedImage = fileImage;
    }

    const locationString = `${location.latitude},${location.longitude}`;

    if (actionType === "check-in") {
      formData.append("checkInLocation", locationString);
      formData.append("checkInImage", capturedImage);
      try {
        const response = await api.post("/punch/in", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (response.status === 200) {
          await fetchAttendanceRecords();
        }
      } catch (err) {
        setError(err.response?.data?.message || "Failed to check in.");
      }
    } else if (actionType === "check-out") {
      const data = await fetchAttendanceRecords();
      if (!data || data.length === 0) {
        setError("No attendance records found.");
        return;
      }
      const sortedRecords = [...data].sort(
        (a, b) => new Date(b.checkIn) - new Date(a.checkIn)
      );
      const latestRecord = sortedRecords[0];
      if (!latestRecord || latestRecord.checkOut) {
        setError("Already checked out or no valid check-in record found.");
        return;
      }
      const workingHours = formatWorkingHours(latestRecord.checkIn, new Date().toISOString());
      formData.append("checkOutImage", capturedImage);
      formData.append("checkOutLocation", locationString);
      formData.append("Hours", workingHours);
      try {
        const response = await api.post("/punch/out", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (response.status === 200) {
          // Optimistic update using checkIn timestamp as a fallback unique key.
          const updatedCheckOutTime = new Date().toISOString();
          setAttendanceRecords((prevRecords) =>
            prevRecords.map((record) =>
              record.checkIn === latestRecord.checkIn
                ? { ...record, checkOut: updatedCheckOutTime, Hours: workingHours }
                : record
            )
          );
          await fetchAttendanceRecords();
        }
      } catch (err) {
        setError(err.response?.data?.message || "Failed to check out.");
      }
    }
  };

  const handleCheck = (type) => {
    setActionType(type);
    setCameraModalOpen(true);
  };

  // Compute paginated records
  const paginatedRecords = attendanceRecords.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const renderTableView = () => (
    <TableContainer component={Paper} sx={{ mt: 4 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell><strong>Check In Time</strong></TableCell>
            <TableCell><strong>Check In Image</strong></TableCell>
            <TableCell><strong>Check Out Time</strong></TableCell>
            <TableCell><strong>Check Out Image</strong></TableCell>
            <TableCell><strong>Working Hours</strong></TableCell>
            <TableCell><strong>Check In Location</strong></TableCell>
            <TableCell><strong>Check Out Location</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedRecords.map((record, index) => (
            <TableRow key={record.id || index}>
              <TableCell>
                {record.checkIn ? new Date(record.checkIn).toLocaleString() : "-"}
              </TableCell>
              <TableCell>
                {record.checkInImage ? (
                  <img
                    src={record.checkInImage}
                    alt="Check In"
                    style={{
                      width: "40px",
                      height: "20px",
                      objectFit: "cover",
                      borderRadius: "4px",
                    }}
                  />
                ) : "-"}
              </TableCell>
              <TableCell>
                {record.checkOut ? new Date(record.checkOut).toLocaleString() : "-"}
              </TableCell>
              <TableCell>
                {record.checkOutImage ? (
                  <img
                    src={record.checkOutImage}
                    alt="Check Out"
                    style={{
                      width: "40px",
                      height: "20px",
                      objectFit: "cover",
                      borderRadius: "4px",
                    }}
                  />
                ) : "-"}
              </TableCell>
              <TableCell>{record.Hours || "-"}</TableCell>
              <TableCell>{record.checkInLocation || "-"}</TableCell>
              <TableCell>{record.checkOutLocation || "-"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderAccordionView = () => (
    <Box sx={{ mt: 4 }}>
      {paginatedRecords.map((record, index) => (
        <Accordion key={record.id || index}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">
              {record.checkIn ? new Date(record.checkIn).toLocaleString() : "No Check In"} –{" "}
              {record.checkOut ? new Date(record.checkOut).toLocaleString() : "Not Checked Out"}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2">
              <strong>Check In:</strong> {record.checkIn ? new Date(record.checkIn).toLocaleString() : "-"}
            </Typography>
            {record.checkInImage && (
              <Box sx={{ my: 1 }}>
                <Typography variant="body2"><strong>Check In Image:</strong></Typography>
                <img
                  src={record.checkInImage}
                  alt="Check In"
                  style={{
                    width: "100px",
                    height: "50px",
                    objectFit: "cover",
                    borderRadius: "4px",
                    display: "block",
                    marginTop: "4px",
                  }}
                />
              </Box>
            )}
            
            <Typography variant="body2">
              <strong>Check Out:</strong> {record.checkOut ? new Date(record.checkOut).toLocaleString() : "-"}
            </Typography>
            {record.checkOutImage && (
              <Box sx={{ my: 1 }}>
                <Typography variant="body2"><strong>Check Out Image:</strong></Typography>
                <img
                  src={record.checkOutImage}
                  alt="Check Out"
                  style={{
                    width: "100px",
                    height: "50px",
                    objectFit: "cover",
                    borderRadius: "4px",
                    display: "block",
                    marginTop: "4px",
                  }}
                />
              </Box>
            )}
            
            <Typography variant="body2">
              <strong>Working Hours:</strong> {record.Hours || "-"}
            </Typography>
            <Typography variant="body2">
              <strong>Check In Location:</strong> {record.checkInLocation || "-"}
            </Typography>
            <Typography variant="body2">
              <strong>Check Out Location:</strong> {record.checkOutLocation || "-"}
            </Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
  
  // Fallback back function in case there's no history
  const handleBack = () => {
    navigate("/dashboard"); // default route if no history exists
  }


  return (
    <Container maxWidth="md">
      <Paper elevation={4} sx={{ p: 4, my: 4, borderRadius: 2 }}>
        {/* Arrow back button */}
        <Typography variant="h5" gutterBottom>
        <IconButton onClick={(handleBack)} sx={{ mb: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        
          Attendance System
        </Typography>
        {loading ? (
          <CircularProgress />
        ) : (
          <Box>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1">IP Address: {ip}</Typography>
              <Typography variant="body1">
                Location:{" "}
                {location
                  ? `Latitude ${location.latitude}, Longitude ${location.longitude}`
                  : "Retrieving..."}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
              {status === "out" ? (
                <Button variant="contained" color="primary" onClick={() => handleCheck("check-in")}>
                  Check In
                </Button>
              ) : (
                <Button variant="contained" color="secondary" onClick={() => handleCheck("check-out")}>
                  Check Out
                </Button>
              )}
            </Box>
            {attendanceRecords.length > 0 &&
              (isMobile ? renderAccordionView() : renderTableView())}
            {attendanceRecords.length > rowsPerPage && (
              <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                <Pagination
                  count={Math.ceil(attendanceRecords.length / rowsPerPage)}
                  page={page}
                  onChange={(event, value) => setPage(value)}
                  color="primary"
                />
              </Box>
            )}
          </Box>
        )}
      </Paper>
      <CameraModal open={cameraModalOpen} onClose={() => setCameraModalOpen(false)} onCapture={handleCapture} />
    </Container>
  );
};

export default Attendance;
